import { useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"
import {
  Dropdown,
  UserOutlined,
  LockOutlined,
  BellOutlined,
  LogoutOutlined,
  BankOutlined,
} from "@mimber/ui-web"
import type { ItemType } from "@mimber/ui-web"
import { useProfile } from "@mimber/client/mimber-organization"
import { useHelpGuideMenuItem } from "@/core/hooks/useHelpGuideMenuItem"
import { useReportIssueMenuItem } from "@/core/hooks/useReportIssueMenuItem"
import { useContactSupportMenuItem } from "@/core/hooks/useContactSupportMenuItem"
import { ProfileAvatar } from "../../blocks/ProfileAvatar"
import type { ProfileDropdownProps } from "./ProfileDropdown.types"

export const ProfileDropdown: React.FunctionComponent<ProfileDropdownProps> = ({
  className,
}) => {
  const navigate = useNavigate()
  const { data: profileResponse } = useProfile()
  const helpGuideMenuItem = useHelpGuideMenuItem()
  const reportIssueMenuItem = useReportIssueMenuItem()
  const { menuItem: contactSupportMenuItem, modal: contactSupportModal } =
    useContactSupportMenuItem()

  const profile = profileResponse?.data
  const fullName = [profile?.firstName, profile?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim()
  const displayName = fullName || profile?.email || "Signed In User"
  const displayEmail = profile?.email || ""
  const shouldShowSecondaryEmail =
    Boolean(displayEmail) &&
    displayEmail.trim().toLowerCase() !== displayName.trim().toLowerCase()

  const profileMenuItems: ItemType[] = useMemo(
    () => [
      {
        key: "profile-summary",
        icon: <UserOutlined />,
        onClick: () =>
          navigate({
            to: "/account-settings/$tabKey",
            params: { tabKey: "profile" },
          }),
        label: (
          <div className="flex min-w-55 flex-col gap-0.5 leading-[1.3]">
            <span className="text-xs uppercase tracking-wide text-muted-foreground/80">
              Signed in as
            </span>
            <span className="truncate text-sm font-medium text-foreground">
              {displayName}
            </span>
            {shouldShowSecondaryEmail ? (
              <span className="truncate text-xs text-muted-foreground">
                {displayEmail}
              </span>
            ) : null}
          </div>
        ),
      },
      { type: "divider" },
      {
        key: "account-settings",
        icon: <UserOutlined />,
        label: "Profile",
        onClick: () =>
          navigate({
            to: "/account-settings/$tabKey",
            params: { tabKey: "profile" },
          }),
      },
      {
        key: "account-security",
        icon: <LockOutlined />,
        label: "Account & Security",
        onClick: () =>
          navigate({
            to: "/account-settings/$tabKey",
            params: { tabKey: "security" },
          }),
      },
      {
        key: "notifications",
        icon: <BellOutlined />,
        label: "Notifications",
        onClick: () =>
          navigate({
            to: "/account-settings/$tabKey",
            params: { tabKey: "notifications" },
          }),
      },
      { type: "divider" },
      {
        key: "manage-organizations",
        icon: <BankOutlined />,
        label: "Manage Organizations",
        onClick: () => navigate({ to: "/organizations" }),
      },
      { type: "divider" },
      helpGuideMenuItem,
      reportIssueMenuItem,
      contactSupportMenuItem,
      { type: "divider" },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: "Log Out",
        danger: true,
        onClick: () => navigate({ to: "/logout" }),
      },
    ],
    [
      displayEmail,
      displayName,
      navigate,
      shouldShowSecondaryEmail,
      helpGuideMenuItem,
      reportIssueMenuItem,
      contactSupportMenuItem,
    ]
  )

  return (
    <>
      {contactSupportModal}
      <Dropdown
        menu={{ items: profileMenuItems }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <button
          type="button"
          className={className}
          title="User Menu"
          aria-label="User menu"
        >
          <ProfileAvatar size="large" />
        </button>
      </Dropdown>
    </>
  )
}
